import { createContext, use, useEffect, useState } from "react";
import type { JSX, ReactNode } from "react";
import type { EventSource } from "../api/schemas";
import type { IdGenerator } from "../domain/ids";
import type { LocalStore } from "./LocalStore";
import type {
  ClockSnapshot,
  LocalAttribution,
  LocalEvent,
  ProjectCache,
  ProjectPreferences,
} from "./model";

/** Offline services injected from the composition root. */
export interface OfflineServices {
  readonly store: LocalStore;
  readonly ids: IdGenerator;
  /** Device clock in epoch milliseconds. */
  readonly nowMs: () => number;
  /** Source recorded with events of this platform (`Web`, `Pwa`, later `Ios`/`Android`). */
  readonly eventSource: EventSource;
  /** IANA time zone of the device, used if the employment has none. */
  readonly deviceTimeZone: string;
}

const OfflineContext = createContext<OfflineServices | null>(null);

/** Properties of {@link OfflineProvider}. */
export interface OfflineProviderProps {
  readonly services: OfflineServices;
  readonly children: ReactNode;
}

/**
 * Provides the local store and helpers.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function OfflineProvider(props: OfflineProviderProps): JSX.Element {
  const provider = <OfflineContext value={props.services}>{props.children}</OfflineContext>;

  return provider;
}

/**
 * Returns the offline services.
 *
 * @returns The services.
 * @throws Error if no {@link OfflineProvider} is present.
 */
export function useOffline(): OfflineServices {
  const services = use(OfflineContext);

  if (services === null) {
    throw new Error("useOffline must be used inside an OfflineProvider.");
  }

  return services;
}

/** Result of {@link useLocalEvents}. */
export interface LocalEventsState {
  /** `false` until the first read finished. */
  readonly loaded: boolean;
  readonly events: readonly LocalEvent[];
}

/**
 * Observes the local events of a person (updates on every change, also from other tabs).
 *
 * @param ownerSubject - OIDC subject or `null` if nobody is signed in.
 * @returns The events.
 */
export function useLocalEvents(ownerSubject: string | null): LocalEventsState {
  const { store } = useOffline();
  const [state, setState] = useState<LocalEventsState>({ loaded: false, events: [] });

  useEffect(() => {
    if (ownerSubject === null) {
      return undefined;
    }

    const unsubscribe = store.watchEvents(ownerSubject, (events) => {
      setState({ loaded: true, events });
    });

    return unsubscribe;
  }, [store, ownerSubject]);

  const current = ownerSubject === null ? { loaded: true, events: [] } : state;
  return current;
}

/**
 * Observes the server snapshot of a person.
 *
 * @param ownerSubject - OIDC subject or `null`.
 * @returns The snapshot (`undefined` while loading, `null` if none is stored).
 */
export function useClockSnapshot(ownerSubject: string | null): ClockSnapshot | null | undefined {
  const { store } = useOffline();
  const [snapshot, setSnapshot] = useState<ClockSnapshot | null | undefined>(undefined);

  useEffect(() => {
    if (ownerSubject === null) {
      return undefined;
    }

    const unsubscribe = store.watchSnapshot(ownerSubject, setSnapshot);

    return unsubscribe;
  }, [store, ownerSubject]);

  const current = ownerSubject === null ? null : snapshot;
  return current;
}

/**
 * Observes the project attributions of a person (T-124).
 *
 * @param ownerSubject - Owner key or `null` if nobody is signed in.
 * @returns The attributions in the order they were recorded.
 */
export function useLocalAttributions(ownerSubject: string | null): readonly LocalAttribution[] {
  const { store } = useOffline();
  const [attributions, setAttributions] = useState<readonly LocalAttribution[]>([]);

  useEffect(() => {
    if (ownerSubject === null) {
      return undefined;
    }

    const unsubscribe = store.watchAttributions(ownerSubject, setAttributions);

    return unsubscribe;
  }, [store, ownerSubject]);

  const current = ownerSubject === null ? [] : attributions;
  return current;
}

/**
 * Observes the cached projects of a person (T-124).
 *
 * @param ownerSubject - Owner key or `null`.
 * @returns The cache (`null` while loading or if none is stored).
 */
export function useProjectCache(ownerSubject: string | null): ProjectCache | null {
  const { store } = useOffline();
  const [cache, setCache] = useState<ProjectCache | null>(null);

  useEffect(() => {
    if (ownerSubject === null) {
      return undefined;
    }

    const unsubscribe = store.watchProjectCache(ownerSubject, setCache);

    return unsubscribe;
  }, [store, ownerSubject]);

  const current = ownerSubject === null ? null : cache;
  return current;
}

/**
 * Observes favorites and recently used projects of a person (T-124).
 *
 * @param ownerSubject - Owner key or `null`.
 * @returns The preferences (empty while loading).
 */
export function useProjectPreferences(ownerSubject: string | null): ProjectPreferences {
  const { store } = useOffline();
  const [preferences, setPreferences] = useState<ProjectPreferences | null>(null);

  useEffect(() => {
    if (ownerSubject === null) {
      return undefined;
    }

    const unsubscribe = store.watchProjectPreferences(ownerSubject, setPreferences);

    return unsubscribe;
  }, [store, ownerSubject]);

  const current = preferences ?? { ownerSubject: ownerSubject ?? "", favorites: [], recent: [] };
  return current;
}
